import { v4 as uuid } from 'uuid';
import type { RxCollection } from 'rxdb';
import {
    timeEntryInputSchema,
    normalizePersonName,
    type TimeEntryDocType,
    type AccountResolver,
    type MatchableAccount
} from '@ast-data/shared';
import { isoWeekYear, isFutureBooking } from './timeMath';
import { queryTimeEntryOverlaps } from './overlap';
import { type TimeEntryVariant } from './timeEntryVariant';

/** The editable fields of a time entry, as the grid and dialog collect them. */
export interface TimeEntryValues {
    date: string;
    workerName: string;
    /** How many of them — 1 on a variant that does not offer the Anzahl. */
    quantity: number;
    startTime: string;
    endTime: string;
    pauseMinutes: number;
    note: string;
    phaseNumber: string;
}

export type SaveTimeEntryResult =
    | { status: 'ok' }
    /** A shared-schema violation; `message` is the rule code for i18n. */
    | { status: 'invalid'; message: string | undefined }
    /** Blocked: the worker is already booked in an overlapping span that day. */
    | { status: 'overlap'; overlap: TimeEntryDocType };

/**
 * The single save path for a time entry (insert or update), shared by the
 * inline grid and the form dialog of BOTH variants (Mitarbeiter and Geräte).
 * Validates with the shared schema, derives year/week + — for worker entries —
 * the optional user link, and blocks a save that would double-book the worker
 * or Gerät (same day, overlapping times) on any visible project.
 *
 * The name -> account link goes through `resolveAccount` (see
 * `users/accountMatching`), the one rule the whole app matches names by:
 * spelling is normalized away, a short form resolves where it can only mean one
 * person, and an ambiguous name links to nobody.
 *
 * `linkUserId` names the account outright, for callers that already know it —
 * the Zeitabgleich's takeover knows exactly whose day it is writing.
 *
 * On an UPDATE whose name is unchanged, the link the entry already carries is
 * kept rather than derived again: it may never have come from the name at all,
 * and correcting an end time is no reason to file the day under nobody.
 */
export async function saveTimeEntry(
    collection: RxCollection<TimeEntryDocType>,
    variant: TimeEntryVariant,
    resolveAccount: AccountResolver<MatchableAccount>,
    projectId: string,
    values: TimeEntryValues,
    existingId?: string,
    linkUserId?: string
): Promise<SaveTimeEntryResult> {
    const workerName = values.workerName.trim();
    // Machines take no break — an equipment row's net hours are its full span.
    const pauseMinutes = variant.showPause ? values.pauseMinutes : 0;
    // A person is one person: a variant without the Anzahl stores the 1 that
    // leaves every total exactly as it was.
    const quantity = variant.showQuantity ? values.quantity : 1;
    const parsed = timeEntryInputSchema.safeParse({
        entryType: variant.type,
        date: values.date,
        workerName,
        quantity,
        userId: '',
        startTime: values.startTime,
        endTime: values.endTime,
        pauseMinutes,
        note: values.note,
        phaseNumber: values.phaseNumber
    });
    if (!parsed.success) {
        return { status: 'invalid', message: parsed.error.issues[0]?.message };
    }

    // No date/time beyond the bookable bound — the shift being written down may
    // reach an hour past the clock, nothing further (see FUTURE_BUFFER_MINUTES).
    // A Gerät's "ganzer Tag" is the exception (see isFutureBooking).
    if (
        isFutureBooking(
            values.date,
            values.startTime,
            values.endTime,
            variant.showAllDay
        )
    ) {
        return { status: 'invalid', message: 'future' };
    }

    const now = new Date().toISOString();
    const id = existingId ?? uuid();
    const { year, week } = isoWeekYear(values.date);
    const existing = existingId
        ? await collection.findOne(existingId).exec()
        : null;
    // The link this entry already carries, where the name has not changed —
    // spelling tidied counts as unchanged, it is the same person (see above).
    const keptUser =
        existing &&
        normalizePersonName(existing.workerName) ===
            normalizePersonName(workerName)
            ? existing.userId
            : '';
    // Kept first, then the name rule; `linkUserId` overrides both, including
    // with '' — a caller that says "no account" has decided that too.
    const derivedUser = keptUser || (resolveAccount(workerName)?.id ?? '');
    // Only people have accounts — a Gerät's name never links to one.
    const matchedUser = variant.linkUsers ? (linkUserId ?? derivedUser) : '';
    const data = {
        projectId,
        entryType: variant.type,
        date: values.date,
        workerName,
        quantity: parsed.data.quantity,
        userId: matchedUser,
        startTime: values.startTime,
        endTime: values.endTime,
        pauseMinutes,
        note: values.note.trim(),
        phaseNumber: values.phaseNumber.trim(),
        year,
        week
    };

    const overlaps = await queryTimeEntryOverlaps(collection, {
        id,
        ...data,
        createdBy: '',
        updatedBy: '',
        createdAt: now,
        updatedAt: now
    });
    if (overlaps.length > 0) {
        return { status: 'overlap', overlap: overlaps[0] };
    }

    if (existingId) {
        await existing?.incrementalPatch({ ...data, updatedAt: now });
    } else {
        await collection.insert({
            id,
            ...data,
            createdBy: '',
            updatedBy: '',
            createdAt: now,
            updatedAt: now
        });
    }
    return { status: 'ok' };
}
